// =========================================================================
// `useWorkflowStepContext()` — the one place a `WorkflowStepContext` is built.
//
// `shared/seed/workflow-steps.ts` models all twenty-one steps as pure
// functions of an explicit context. That seed imports no data module by
// design, so somebody has to assemble the value. This is that somebody, and
// it is deliberately the only one: twenty-one steps sharing one assembly is
// what keeps their numbers agreeing with each other and with Tier 1.
//
// What the assembly is
// --------------------
// Three sources, in order of cost:
//
//   1. **Eager core** (`data/core.ts`) — fleet, mines, classes, the condition
//      register, the rate card, the per-machine cost roll-up. Free; already
//      in the bundle.
//   2. **Eight lazy domains** — work orders, telemetry, service, parts,
//      people, costs, the document index and the site register. Fetched in
//      parallel by `useOemData`'s module-level promise cache, so a second
//      step page pays nothing.
//   3. **The Maximo service register** — 100 files, 1.6 MB. Loaded *only*
//      when the step in view reads it (see {@link stepNeedsMaximo}). Three of
//      the twenty-one do.
//
// The two mutations the demo made, made here instead
// --------------------------------------------------
// The demo mutated `HEALTH` in place to stamp `h.band`, and `pastInterval()`
// returned those same mutated rows. This port mutates nothing, so both are
// decorated on the way in — `healthBand()` is the derivation and the register
// itself is left alone. `workflow-steps.ts` documents this as the caller's
// job; this is the caller.
//
// Time
// ----
// `daysTo` comes from `shared/oem-clock.ts`, which measures against the frozen
// `OEM_TODAY`. Nothing here reads a wall clock, so a step's "12 d overdue"
// says the same thing in September as it will in March.
//
// Fork-owned (zone C), P5a lane. Shared across all three workflow scenes.
// =========================================================================

import { useEffect, useMemo, useState } from 'react';
import {
  BY_ID,
  CLASSES,
  COST_PER_MACHINE,
  FLEET,
  HEALTH,
  MINES,
  MINE_ORDER,
  RATES,
} from '@/versions/v1/data/core';
import { unpackTable } from '@/versions/v1/shared/columnar';
import { daysTo } from '@/versions/v1/shared/oem-clock';
import {
  FLEET_RULES,
  dataModeLabel,
  fmt$,
  fmtk,
  healthBand,
  healthOf,
  intervalOf,
  meanUtil,
  mxOf,
  pastInterval,
  predictedUsage,
  renewals,
  revenue,
  sent1,
  statusOf,
  techStats,
  upgradeCandidates,
  warrantyActive,
  jobs as buildJobs,
} from '@/versions/v1/shared/oem-engine';
import { ALERTS } from '@/versions/v1/shared/seed/alerts';
import { loadMachineRecord, useOemData } from '@/versions/v1/shared/useOemData';
import type { MineCode } from '@/versions/v1/schema/machine';
import type { MaximoAsset } from '@/versions/v1/schema/service';
import type { WorkOrder } from '@/versions/v1/schema/work-order';
import type { ShiftDailyRow } from '@/versions/v1/schema/telemetry';
import type { CustomerSatisfactionResponse } from '@/versions/v1/schema/person';
import type {
  WorkflowStepContext,
  WorkflowStepId,
} from '@/versions/v1/shared/seed/workflow-steps';

/**
 * The three steps that read the Meridian service register.
 *
 * `reg` prints a record per machine and counts the register; `entitle` and
 * `value` read warranty and contract type through `warrantyActive()` and
 * `upgradeCandidates()`. The other eighteen never touch it, and making them
 * all wait on 100 requests would be a page-weight decision taken on behalf of
 * pages that do not need it.
 */
const MAXIMO_STEPS: ReadonlySet<string> = new Set<WorkflowStepId>(['reg', 'entitle', 'value']);

/** Does this step (or this module page) need the Maximo register loaded? */
export const stepNeedsMaximo = (stepId: string | undefined): boolean =>
  stepId !== undefined && MAXIMO_STEPS.has(stepId);

/**
 * Machine id → Maximo asset record.
 *
 * A machine whose file failed is **absent**, not present-and-undefined, so the
 * map satisfies the seed's `Record<string, MaximoAsset>` while `mxOf` still
 * reads a miss as `null`.
 */
export type MaximoRegister = Readonly<Record<string, MaximoAsset>>;

/**
 * Fetch the whole service register, one file per machine, in parallel.
 *
 * `loadMachineRecord` is cached per machine by `useOemData`'s promise cache,
 * so this is 100 requests once per session and nothing thereafter — including
 * for a machine page the reader opened earlier.
 *
 * A single failed record does not fail the register: the machine is simply
 * absent, and `mxOf` already normalises a miss. Blanking twenty-one pages
 * because one of a hundred files 404'd would be the wrong trade.
 */
function loadMaximoRegister(): Promise<MaximoRegister> {
  return Promise.all(
    FLEET.map((f) =>
      loadMachineRecord(f.machine_id).then(
        (record): readonly [string, MaximoAsset] | null => [f.machine_id, record.mx],
        (): null => null,
      ),
    ),
  ).then((pairs) => Object.fromEntries(pairs.filter((p) => p !== null)));
}

/**
 * `contractor` is absent as `null` in the data pack and as `undefined` in the
 * seed's row shape. One spelling of "no contractor on this site", adapted at
 * the seam rather than by widening the seed.
 */
const optionalContractor = <T extends { readonly contractor: string | null }>(
  row: T,
): Omit<T, 'contractor'> & { readonly contractor?: string } => ({
  ...row,
  contractor: row.contractor ?? undefined,
});

/**
 * Narrow a columnar table to the seed's labelled-tuple view of it.
 *
 * `roster` ships 10 columns and the seed's `RosterRow` names 7; `css` ships 13
 * and `CssRow` names 12. The named prefix is identical and is the only part
 * any step indexes — the trailing columns are simply unnamed. TypeScript has
 * no "tuple starts with" relation, so the narrowing is asserted here, once,
 * with the column orders verified against the extracted pack. Nothing is
 * dropped at runtime: the same rows travel through.
 */
const narrowColumns = <T,>(table: unknown): T => table as T;

/** What {@link useWorkflowStepContext} returns. */
export interface WorkflowContextState {
  /** The assembled context, or `null` until every domain it needs has landed. */
  readonly ctx: WorkflowStepContext | null;
  /** True while any domain is still in flight. */
  readonly loading: boolean;
  /** The first domain error, or `null`. */
  readonly error: Error | null;
  /**
   * True when the page asked for the Maximo register and it has not arrived.
   * `ctx` is still `null` in that case; the flag lets a scene say *which*
   * fetch it is waiting on rather than showing an undifferentiated skeleton.
   */
  readonly maximoPending: boolean;
  /**
   * The two schema-typed values the assembly builds on the way through.
   *
   * The context narrows them to the seed's structural subsets, which is the
   * right shape for a workflow step and the wrong one for anything that needs
   * to call back into the engine. Rather than round-trip through the narrowed
   * form and lose the fields, they are handed out here at full width.
   */
  readonly raw: {
    readonly workOrders: readonly WorkOrder[];
    readonly maximo: MaximoRegister;
  } | null;
}

/**
 * Build the context the twenty-one workflow steps read.
 *
 * @param needsMaximo - load the 100-file service register as well. Pass
 *   {@link stepNeedsMaximo} of the step in view; the default is `false`, which
 *   leaves `pack.mx` empty and the two Maximo-derived lists empty rather than
 *   wrong.
 */
export function useWorkflowStepContext(needsMaximo = false): WorkflowContextState {
  const wos = useOemData('work-orders');
  const telemetry = useOemData('telemetry');
  const service = useOemData('service');
  const parts = useOemData('parts');
  const people = useOemData('people');
  const costs = useOemData('costs');
  const docIndex = useOemData('doc-index');
  const sites = useOemData('sites');

  const [maximo, setMaximo] = useState<MaximoRegister | null>(null);

  useEffect(() => {
    if (!needsMaximo) return;
    let live = true;
    loadMaximoRegister().then((register) => {
      if (live) setMaximo(register);
    });
    return () => {
      live = false;
    };
  }, [needsMaximo]);

  const states = [wos, telemetry, service, parts, people, costs, docIndex, sites];
  const domainsLoading = states.some((s) => s.loading);
  const error = states.find((s) => s.error)?.error ?? null;
  const maximoPending = needsMaximo && maximo === null;

  const ctx = useMemo((): WorkflowStepContext | null => {
    if (
      !wos.data ||
      !telemetry.data ||
      !service.data ||
      !parts.data ||
      !people.data ||
      !costs.data ||
      !docIndex.data ||
      !sites.data ||
      maximoPending
    ) {
      return null;
    }

    // The register, empty when the page did not ask for it. `mxOf` reads a
    // miss as `null`, so the Maximo-derived lists come back empty rather than
    // wrong — and no step outside `MAXIMO_STEPS` consults them.
    const register: MaximoRegister = maximo ?? {};
    const maximoCtx = { maximo: register };

    const workOrders = unpackTable<WorkOrder>(wos.data.wos);
    const shiftDays = unpackTable<ShiftDailyRow>(telemetry.data.shift_daily);
    const css = unpackTable<CustomerSatisfactionResponse>(people.data.css);

    // The demo stamped `band` onto every register row in place. Decorated here
    // instead, for both the register and the past-interval slice of it.
    const health = HEALTH.map((h) => ({ ...h, band: healthBand(h) }));

    const jobCtx = {
      workOrders,
      techJobs: service.data.tapp_jobs,
      cases: service.data.cases,
    };
    const techCtx = {
      techJobs: service.data.tapp_jobs,
      techs: people.data.tapp_techs,
      css,
      workOrders,
      timesheets: costs.data.timesheets,
      training: service.data.lms,
    };
    const partsCtx = { parts: parts.data.parts, inventory: parts.data.inventory };

    // ---- adapters ----------------------------------------------------
    // Seven places where the schema layer and the seed's structural subsets
    // disagree. None is a data difference; all are spelling. They are
    // adapted here, at the seam, rather than by widening either side.
    const contracts = service.data.mx_contracts.map(optionalContractor);
    const catbaseSections = parts.data.catbase_sections.map((s) => ({
      ...s,
      // The pack numbers catalogue sections; the seed prints them.
      section: String(s.section),
      // `note` is absent as `null` in the pack, `undefined` in the seed.
      note: s.note ?? undefined,
    }));
    const openJobs = buildJobs(jobCtx).map((j) => ({
      ...j,
      machine: j.machine ?? undefined,
    }));

    return {
      fleet: FLEET,
      machineById: BY_ID,
      classes: CLASSES,
      mineOrder: MINE_ORDER,
      mines: MINES,
      health,
      workOrders,
      shiftDays,
      alerts: ALERTS,
      fleetRules: FLEET_RULES,

      pack: {
        mx: register,
        mx_contracts: contracts,
        mx_pm: service.data.mx_pm,
        inventory: parts.data.inventory,
        parts: parts.data.parts,
        orders: parts.data.orders,
        cases: service.data.cases,
        bulletins: parts.data.bulletins,
        docs: docIndex.data.docs,
        // Three tables stay in their columnar wire form: the steps that read
        // them index positionally, which is exactly what `{cols, rows}` is.
        events_daily: telemetry.data.events_daily,
        events_critical: telemetry.data.events_critical,
        xreach_sessions: service.data.xreach_sessions,
        tapp_jobs: service.data.tapp_jobs,
        tapp_techs: people.data.tapp_techs,
        timesheets: costs.data.timesheets,
        // `roster` and `css` stay in their columnar wire form because the
        // steps that read them index positionally. The pack's rows carry more
        // columns than the seed's labelled tuples name (10 vs 7, 13 vs 12);
        // the named prefix is identical, and TypeScript has no "tuple starts
        // with" relation, so the extra trailing columns are narrowed away
        // here. Nothing is dropped at runtime.
        roster: narrowColumns<WorkflowStepContext['pack']['roster']>(people.data.roster),
        lms: service.data.lms,
        css: narrowColumns<WorkflowStepContext['pack']['css']>(people.data.css),
        cost_per_machine: COST_PER_MACHINE,
        cost_by_machine_cat: costs.data.cost_by_machine_cat,
        rates: RATES,
        siteinfo: sites.data.siteinfo,
        catbase_bom: parts.data.catbase_bom,
        catbase_sections: catbaseSections,
      },

      derived: {
        jobs: openJobs,
        predictedUsage: predictedUsage(partsCtx),
        // Same in-place `band` stamp as the register above.
        pastInterval: pastInterval().map((h) => ({ ...h, band: healthBand(h) })),
        warrantyActive: warrantyActive(maximoCtx),
        upgradeCandidates: upgradeCandidates(maximoCtx),
        renewals: renewals({ contracts: service.data.mx_contracts }).map(optionalContractor),
        revenue: revenue({ workOrders, techJobs: service.data.tapp_jobs }),
        meanUtil: meanUtil(techCtx),
      },

      helpers: {
        // The seed's contract is `| undefined`; the engine's is `| null`.
        mxOf: (id: string) => mxOf(id, maximoCtx) ?? undefined,
        healthOf: (id: string) => healthOf(id).map((h) => ({ ...h, band: healthBand(h) })),
        statusOf,
        intervalOf,
        daysTo,
        techStats: (name: string) => techStats(name, techCtx),
        // `MineInfo.id` is a plain string in the seed and a `MineCode` in the
        // engine. The six codes are the same six; the seed simply does not
        // carry the union.
        dataModeLabel: (mine) =>
          dataModeLabel({
            id: mine.id as MineCode,
            data_mode: mine.data_mode,
            mining: mine.mining ?? '',
          }),
        firstSentence: sent1,
        fmtk,
        fmtMoney: fmt$,
      },
    };
  }, [
    wos.data,
    telemetry.data,
    service.data,
    parts.data,
    people.data,
    costs.data,
    docIndex.data,
    sites.data,
    maximo,
    maximoPending,
  ]);

  const raw = useMemo(
    () =>
      wos.data ? { workOrders: unpackTable<WorkOrder>(wos.data.wos), maximo: maximo ?? {} } : null,
    [wos.data, maximo],
  );

  return { ctx, raw, loading: domainsLoading || maximoPending, error, maximoPending };
}
